/**
 * Commerce-bearing wording, resolved per platform in ONE place. Web strings are
 * the ones the app has always shown; native strings follow the rule in
 * policy.ts. `null` means "render nothing here on native".
 */

import { REFERRAL_DISCOUNT_PERCENT } from '@/lib/referral/types';
import type { TierConfig } from '@/lib/subscription/types';
import { ifCommerce, NOT_IN_PLAN, COMMERCE_ENABLED } from './policy';

export interface Faq {
  q: string;
  a: string;
}

export const COPY = {
  teamLockedHint: ifCommerce('Upgrade to add team members', NOT_IN_PLAN),
  teamLockedBadge: ifCommerce('Upgrade', undefined),
  referralSettings: ifCommerce(
    { label: 'Refer & Earn', description: `Get ${REFERRAL_DISCOUNT_PERCENT}% off for you & friends` },
    null,
  ),
  referralProfileHint: ifCommerce(`Earn ${REFERRAL_DISCOUNT_PERCENT}% off per referral`, null),
  valuationLockedNote: ifCommerce(
    'Berkus Method included free. Upgrade for all 4 methods.',
    'Berkus Method is included in your current plan.',
  ),
  sharedDdLocked: ifCommerce(
    {
      alertTitle: 'Premium Feature',
      alertBody: 'Shared Due Diligence is available for Premium investors. Upgrade to collaborate with other investors.',
      label: 'Collaborative DD (Premium)',
      description: 'Upgrade to Premium to share DD with other investors',
    },
    {
      alertTitle: 'Collaborative DD',
      alertBody: 'Shared Due Diligence is not included in your current plan.',
      label: 'Collaborative DD',
      description: NOT_IN_PLAN,
    },
  ),
  dealflowLockedSubtitle: ifCommerce('Available on paid plans', NOT_IN_PLAN),
  coldOutreachPlanReason: ifCommerce(
    'Direct outreach to investors is available on a higher plan.',
    'Direct outreach to investors is not included in your current plan.',
  ),
  scoreDetailLocked: ifCommerce(
    { title: 'Upgrade Required', body: 'Detailed score breakdowns are available to Starter tier and above.', cta: 'View Plans' },
    null,
  ),
  // The CTA opens the startup plans screen (claim/[token].tsx handleUpgrade →
  // openPlans). It used to name a one-off paid "full report" priced in CHF — a
  // product that has never existed; the label now says what the button does.
  claimLocked: ifCommerce({ heading: 'Upgrade to unlock', cta: 'See plans' }, null),
} as const;

export const PLAN_FAQS: { startup: Faq[]; investor: Faq[] } = {
  startup: ifCommerce(
    [
      {
        q: "What's included in Pro?",
        a: 'Pro unlocks advanced analytics, valuation wizard, detailed score breakdowns, priority visibility to investors, and team collaboration features. Visit Subscription in settings for the full comparison.',
      },
      {
        q: 'How do I manage my subscription?',
        a: 'Go to Settings > Subscription & Billing. You can upgrade, downgrade, or cancel anytime. Cancellations take effect at the end of your current billing period.',
      },
    ],
    [],
  ),
  investor: ifCommerce(
    [
      {
        q: "What's included in Premium?",
        a: 'Premium unlocks portfolio tracking, shared due diligence, advanced deal comparison, priority access to new deals, and extended AI analysis capabilities. Visit Subscription in your settings for full details.',
      },
      {
        q: 'How do I manage my subscription?',
        a: 'Go to Settings > Subscription & Billing. You can upgrade, downgrade, or cancel anytime. Cancellations take effect at the end of your current billing period.',
      },
    ],
    [],
  ),
};

/**
 * Plan name, with its monthly price on web only. Takes the whole tier config
 * (rather than a bare price argument) so the price field access itself stays
 * inside this chokepoint file, not at call sites elsewhere in the app — see
 * scripts/ci/check-native-commerce.ts rule R2.
 */
export function planLabel(tier: Pick<TierConfig, 'name' | 'monthlyPrice'>): string {
  return COMMERCE_ENABLED && tier.monthlyPrice > 0 ? `${tier.name} · CHF ${Math.round(tier.monthlyPrice / 100)}/mo` : tier.name;
}
